/*
 * Mobile-only carousel.
 *
 * The desktop design lays all three items out side by side with no controls, so
 * there is no carousel to run above 1024 — the element measures the track,
 * drives the arrows and mirrors the caption only while the mobile layout is on,
 * and clears its state on the way out so nothing it did survives the switch.
 *
 * Swiping is native: the track is a scroll-snap scroller and the arrows just
 * scroll it, so momentum and rubber-banding stay the platform's. The index is
 * read back off the scroller because a swipe never goes through goTo().
 */
class StandaloneProcess extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-proc-track]');
    this.slides = this.track ? Array.from(this.track.children) : [];
    this.prevButton = this.querySelector('[data-proc-prev]');
    this.nextButton = this.querySelector('[data-proc-next]');
    this.captionTitle = this.querySelector('[data-proc-caption-title]');
    this.captionText = this.querySelector('[data-proc-caption-text]');
    this.counter = this.querySelector('[data-proc-index]');

    if (!this.track || this.slides.length === 0) return;

    this.index = 0;
    this.scrollFrame = null;
    this.mobileQuery = window.matchMedia('(max-width: 1023px)');
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.onPrev = () => this.move(-1);
    this.onNext = () => this.move(1);
    this.onScroll = () => this.queueScrollSync();
    this.onKeydown = (event) => this.handleKey(event);
    this.onRefresh = () => this.refresh();

    if (this.prevButton) this.prevButton.addEventListener('click', this.onPrev);
    if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.addEventListener('keydown', this.onKeydown);
    this.mobileQuery.addEventListener('change', this.onRefresh);

    // Watches the track rather than window resize so the theme editor adding or
    // removing a card re-measures too.
    this.resizeObserver = new ResizeObserver(this.onRefresh);
    this.resizeObserver.observe(this.track);

    this.refresh();
  }

  disconnectedCallback() {
    if (this.prevButton) this.prevButton.removeEventListener('click', this.onPrev);
    if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext);
    if (this.track) this.track.removeEventListener('scroll', this.onScroll);
    this.removeEventListener('keydown', this.onKeydown);
    if (this.mobileQuery) this.mobileQuery.removeEventListener('change', this.onRefresh);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
  }

  /* One slide plus one gap. Measured rather than hard-coded because the slide
     narrows with the viewport below the 390 frame. */
  get step() {
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return this.slides[0].getBoundingClientRect().width + gap;
  }

  get maxIndex() {
    return this.slides.length - 1;
  }

  move(direction) {
    this.goTo(this.index + direction);
  }

  goTo(index) {
    const target = Math.min(Math.max(index, 0), this.maxIndex);
    this.index = target;

    this.track.scrollTo({
      left: target * this.step,
      behavior: this.motionQuery.matches ? 'auto' : 'smooth',
    });

    this.sync();
  }

  handleKey(event) {
    if (!this.mobileQuery.matches) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    // The track is focusable, so the browser would scroll it as well.
    event.preventDefault();
    this.move(event.key === 'ArrowLeft' ? -1 : 1);
  }

  queueScrollSync() {
    if (this.scrollFrame) return;

    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      if (!this.mobileQuery.matches) return;

      const step = this.step;
      if (step <= 0) return;

      this.index = Math.min(Math.round(this.track.scrollLeft / step), this.maxIndex);
      this.sync();
    });
  }

  refresh() {
    this.index = Math.min(this.index, this.maxIndex);

    // Desktop shows every card at once with its own caption, so a leftover
    // scroll offset would just hide the first one behind the gutter.
    if (!this.mobileQuery.matches) {
      this.track.scrollLeft = 0;
      this.index = 0;
    }

    this.sync();
  }

  /* The caption lives below the track and is shared, so it has to be told which
     card it is describing. It reads the card's own heading and copy — the same
     nodes desktop shows in place — rather than a second copy of the text. */
  sync() {
    const slide = this.slides[this.index];

    if (slide) {
      const title = slide.querySelector('[data-proc-card-title]');
      const text = slide.querySelector('[data-proc-card-text]');

      if (this.captionTitle) this.captionTitle.textContent = title ? title.textContent : '';
      if (this.captionText) this.captionText.textContent = text ? text.textContent : '';
    }

    if (this.counter) this.counter.textContent = String(this.index + 1);
    if (this.prevButton) this.prevButton.disabled = this.index <= 0;
    if (this.nextButton) this.nextButton.disabled = this.index >= this.maxIndex;
  }
}

customElements.define('standalone-process', StandaloneProcess);
